import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { dealsApi } from '../../api/deals'
import { RefusedError } from '../../api/client'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate, formatMoney } from '../../lib/format'
import { scopeWords } from '../../lib/orgs'
import { routeForPursuit, rowsInFocus } from '../../lib/pipeline'
import type { DealView } from '../../types/deals'
import type { Focus, PipelineTab } from '../../types/pipeline'
import { Correct } from '../../components/Correct'
import { MissedCard, PipelineBand } from './PipelineBand'
import { SuggestionsCard } from './SuggestionsCard'
import { STAGE_ORDER, STAGE_WORDS } from './stages'
import './leads.css'

export { LeadPage } from './LeadPage'

// Leads and Opportunities: the pipeline as the person's own mail tells it. Every row is a thread the
// model read; every number and flag traces to a signal the person can open. You see the org your mailbox
// puts you in unless the switcher says otherwise; the kicker names whichever it is. The analytics band
// on top is computed on the API (nothing is scored here); a pursuit past the lifespan of its kind is a
// MISSED opportunity and leaves the list for
// its own section. Open a row for the lead page: the map, the evidence, the account, the research note.

export function Leads() {
  return <DealsPage tab="leads" title="Leads" lede="First contacts, intros, and pursuits gone quiet, read out of your own sent mail. Each row is a thread; open it for the map of who is who, the messages that put it here, and what the firm already knows about the account." />
}

export function Opportunities() {
  return <DealsPage tab="opportunities" title="Opportunities" lede="Scoped needs, proposals and SOWs out, negotiations, and wins, with the dollar amounts in their own words and the next step still open. Open a row for the full picture." />
}

function DealsPage({ tab, title, lede }: { tab: PipelineTab; title: string; lede: string }) {
  const spoke = useSpoke()
  const navigate = useNavigate()
  const [showDelivery, setShowDelivery] = useState(false)
  const effective = tab === 'opportunities' && showDelivery ? 'delivery' : tab
  const q = useQuery({ queryKey: ['deals', spoke, effective], queryFn: () => dealsApi.list(spoke, effective), staleTime: 15_000 })
  const stats = useQuery({ queryKey: ['deals-stats', spoke, tab], queryFn: () => dealsApi.stats(spoke, tab), staleTime: 30_000, enabled: effective !== 'delivery' })
  const delivery = useQuery({ queryKey: ['deals', spoke, 'delivery-count'], queryFn: () => dealsApi.list(spoke, 'delivery'), staleTime: 60_000, enabled: tab === 'opportunities' })
  const [sort, setSort] = useState<'activity' | 'amount' | 'stage'>('activity')
  const [focus, setFocus] = useState<Focus | null>(null)
  useEffect(() => setFocus(null), [tab, effective])
  const scan = useMutation({ mutationFn: () => dealsApi.scan(spoke, 150), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  const open = (d: DealView) => navigate(`/${tab}/${encodeURIComponent(d.engagement.engagementId)}`)
  // whose pipeline this is, in the org's own name — the switcher in the sidebar chose it
  const scope = scopeWords(q.data?.scope)
  const lifecycle = q.data?.lifecycle ?? {}
  // the play each row is carrying, decided on the API by the same decider the lead page runs
  const plays = q.data?.plays ?? {}
  const evidence = q.data?.evidence ?? {}
  const stateOf = (d: DealView) => (effective === 'delivery' ? 'active' : lifecycle[d.engagement.engagementId] ?? 'active')

  const all = useMemo(() => {
    const by = [...(q.data?.deals ?? [])]
    if (sort === 'amount') by.sort((a, b) => (b.engagement.amount ?? 0) - (a.engagement.amount ?? 0))
    else if (sort === 'stage') by.sort((a, b) => STAGE_ORDER.indexOf(a.engagement.stage) - STAGE_ORDER.indexOf(b.engagement.stage))
    else by.sort((a, b) => (b.engagement.lastActivityOn ?? '').localeCompare(a.engagement.lastActivityOn ?? ''))
    return by
  }, [q.data, sort])

  // the list: what the focus names, else everything that is not a missed opportunity (those sit in their own section)
  const rows = useMemo(() => (focus ? rowsInFocus(all, (d) => d.engagement.engagementId, focus.ids) : all.filter((d) => stateOf(d) !== 'missed')), [all, focus, lifecycle, effective])
  const missedCount = useMemo(() => all.filter((d) => stateOf(d) === 'missed').length, [all, lifecycle, effective])
  const elsewhere = useMemo(() => {
    if (!focus) return []
    const here = new Set(rows.map((d) => d.engagement.engagementId))
    return focus.refs.filter((r) => !here.has(r.engagementId))
  }, [focus, rows])
  const focusOn = (f: Focus | null) => { setFocus(f); if (f) document.getElementById('deal-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }

  return (
    <section className="page deals">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link>{scope ? ` · ${scope}` : ''}</p>
          <h1>{title}</h1>
          <p className="lede">{lede}</p>
        </div>
        <div className="actions">
          {tab === 'opportunities' && delivery.data && delivery.data.total > 0 && (
            <button className={`button ${showDelivery ? 'primary' : ''}`} onClick={() => setShowDelivery(!showDelivery)}>{showDelivery ? 'Back to the pipeline' : `${delivery.data.total} in delivery`}</button>
          )}
          <button className="button" onClick={() => scan.mutate()} disabled={scan.isPending} title="Reads your newest 150 external threads that changed since the last read.">{scan.isPending ? 'Starting…' : 'Read new threads'}</button>
        </div>
      </header>
      {scan.isError && <p className="error">{scan.error instanceof RefusedError ? scan.error.refusal.message : scan.error.message}</p>}

      {effective !== 'delivery' && stats.isPending && <p className="muted">Reading the pipeline…</p>}
      {effective !== 'delivery' && stats.isError && <div className="refusal"><strong>The band could not be computed</strong><p>{stats.error instanceof RefusedError ? stats.error.refusal.message : stats.error.message}</p></div>}
      {effective !== 'delivery' && stats.data && <PipelineBand stats={stats.data} tab={tab} focus={focus} onFocus={focusOn} />}

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && <div className="refusal"><strong>Unavailable</strong><p>{q.error.message}</p></div>}
      {q.isSuccess && all.length === 0 && (
        <div className="card">
          <p className="muted">Nothing here yet. "Read new threads" has the model read your newest external threads and file each one here by what it found.</p>
        </div>
      )}

      {all.length > 0 && (
        <div className="card" id="deal-list">
          <div className="card-head">
            <div>
              <h2>{focus ? focus.label : `Every ${effective === 'delivery' ? 'engagement' : tab === 'leads' ? 'live lead' : 'live opportunity'}`}</h2>
              <p className="muted">
                {focus ? `${rows.length} of ${all.length} in this list${elsewhere.length > 0 ? `; ${elsewhere.length} elsewhere` : ''}.` : 'One row per thread. Open a row for the influence map, the evidence, the account, and the research note.'}
                {!focus && missedCount > 0 && <> <a href="#missed">{missedCount} missed {missedCount === 1 ? 'opportunity sits' : 'opportunities sit'} below.</a></>}
              </p>
            </div>
            {focus && <button className="button small" onClick={() => setFocus(null)}>Show all</button>}
          </div>
          {focus && elsewhere.length > 0 && (
            <div className="focus-strip">
              <strong>Not in this list:</strong>
              <span className="refs">{elsewhere.map((r) => <Link key={r.engagementId} to={routeForPursuit(r)}>{r.companyName} · {STAGE_WORDS[r.stage as keyof typeof STAGE_WORDS] ?? r.stage}</Link>)}</span>
            </div>
          )}
          {rows.length === 0 ? <p className="muted">None of these are in this list.</p> : (
            <table className="grid deals-grid">
              <thead>
                <tr>
                  <th>Company</th>
                  <th><button className="th-sort" onClick={() => setSort('stage')}>Stage</button></th>
                  <th>What is being pursued</th>
                  <th className="num"><button className="th-sort" onClick={() => setSort('amount')}>Amount</button></th>
                  <th>SOW · Proposal</th>
                  <th>Next step</th>
                  <th><button className="th-sort" onClick={() => setSort('activity')}>Last activity</button></th>
                  <th className="correct-col"><span className="visually-hidden">Not right?</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const state = stateOf(d)
                  const owed = state === 'active' && d.engagement.nextStepOwner === 'me' && !!d.engagement.nextStep
                  return (
                    <tr key={d.engagement.engagementId} className={`stage-${d.engagement.stage} ${state} ${owed ? 'owe' : ''}`} onClick={() => open(d)}>
                      <td>
                        <strong>{d.company.name}</strong>
                        <span className="muted small">{d.people.slice(0, 2).map((p) => p.name).join(', ')}{d.people.length > 2 ? ` +${d.people.length - 2}` : ''}</span>
                      </td>
                      <td><span className={`tag stage ${d.engagement.stage}`}>{STAGE_WORDS[d.engagement.stage]}</span></td>
                      <td className="summary-cell">
                        <span className="deal-subject">{d.engagement.subject}</span>
                        {evidence[d.engagement.engagementId] && (
                          <span className={`tag evidence-tag ${evidence[d.engagement.engagementId].score >= 0.7 ? 'strong' : evidence[d.engagement.engagementId].score >= 0.4 ? 'fair' : 'weak'}`} title={`Confidence from the facts on the row: ${evidence[d.engagement.engagementId].basis.join('; ')}.`}>
                            {Math.round(evidence[d.engagement.engagementId].score * 100)}% · {evidence[d.engagement.engagementId].basis[0]}
                          </span>
                        )}
                        <span className="muted small">{d.engagement.summary}</span>
                        {plays[d.engagement.engagementId] && (
                          <span className="tag play-tag" title={`Because ${plays[d.engagement.engagementId].why}. Open the row for the how-to and a line to open with.`}>
                            play: {plays[d.engagement.engagementId].name}
                          </span>
                        )}
                      </td>
                      <td className="num">{d.engagement.amount ? <><strong>{formatMoney(d.engagement.amount)}</strong><span className="muted small">{d.engagement.amountBasis}</span></> : <span className="muted">—</span>}</td>
                      <td>
                        <span className={`flag ${d.engagement.sowSent ? 'on' : ''}`} title={d.engagement.sowName ?? ''}>{d.engagement.sowSent ? `SOW${d.engagement.sowSentOn ? ' ' + formatDate(d.engagement.sowSentOn) : ''}` : 'no SOW'}</span>
                        <span className={`flag ${d.engagement.proposalSent ? 'on' : ''}`}>{d.engagement.proposalSent ? 'proposal' : 'no proposal'}</span>
                      </td>
                      <td className="next-cell">{d.engagement.nextStep ? <><span>{d.engagement.nextStep}</span><span className="muted small">{state === 'missed' ? 'lapsed' : d.engagement.nextStepOwner === 'me' ? 'you owe it' : d.engagement.nextStepOwner === 'them' ? 'they owe it' : ''}{d.engagement.nextStepBy ? ` · by ${formatDate(d.engagement.nextStepBy)}` : ''}</span></> : <span className="muted">—</span>}</td>
                      <td>{d.engagement.lastActivityOn ? formatDate(d.engagement.lastActivityOn) : '—'}{state === 'aging' && <span className="muted small">aging</span>}{state === 'missed' && <span className="muted small">missed</span>}</td>
                      <td className="correct-col">
                        <Correct kind={tab === 'leads' ? 'lead' : 'opportunity'} entityId={d.engagement.engagementId}
                          entityLabel={`${d.company.name} · ${d.engagement.subject}`} currentValue={d.engagement.stage} compact />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {effective !== 'delivery' && !focus && <SuggestionsCard tab={tab} />}
      {effective !== 'delivery' && stats.data && !focus && <MissedCard missed={stats.data.missed} tab={tab} />}
    </section>
  )
}
